
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { path: '/', labelKey: 'nav_top' },
    { path: '/about', labelKey: 'nav_about' },
    { path: '/dream-code', labelKey: 'nav_dreamcode' },
    { path: '/cases', labelKey: 'nav_cases' },
    { path: '/implementation', labelKey: 'nav_implementation' },
    { path: '/awards', labelKey: 'nav_awards' },
    { path: '/contact', labelKey: 'nav_contact', sublabelKey: 'nav_contact_sub' },
  ];

  const languages = [
    { code: 'ja', label: 'JP' },
    { code: 'en', label: 'EN' },
    { code: 'zh', label: 'ZH' },
  ];

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
          isScrolled ? 'h-[70px]' : 'h-[90px]'
        }`}
        style={{ backgroundColor: '#0f2350' }}
      >
        <div className="h-full flex items-center justify-between px-10">
          {/* Logo */}
          <Link to="/" className="flex items-center transition-all duration-400">
            <img
              src="https://static.readdy.ai/image/21eccc7cda4592add8699af0788627b0/f082c97fda16472b44c0e8b65ee5b389.png"
              alt="Dream Realization Academy"
              className={`transition-all duration-400 ${
                isScrolled ? 'h-[54px]' : 'h-[72px]'
              }`}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-10">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="relative text-white text-sm font-medium transition-all duration-300 group whitespace-nowrap"
                style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
              >
                <span className="flex flex-col items-center">
                  <span>{t(item.labelKey)}</span>
                  {item.sublabelKey && (
                    <span className="text-[9px] text-white/60 mt-0.5">{t(item.sublabelKey)}</span>
                  )}
                </span>
                <span
                  className={`absolute bottom-[-4px] left-0 h-[1px] bg-[#D4AF37] transition-all duration-300 ${
                    location.pathname === item.path ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}
                ></span>
              </Link>
            ))}
          </nav>

          {/* Language Switcher - Desktop */}
          <div className="hidden lg:flex items-center gap-4">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={`text-xs font-medium transition-all duration-300 whitespace-nowrap ${
                  i18n.language === lang.code
                    ? 'text-white border-b border-[#D4AF37]'
                    : 'text-white/70 hover:text-white'
                }`}
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                {lang.label}
              </button>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white w-8 h-8 flex flex-col items-center justify-center gap-1.5 cursor-pointer"
            aria-label="メニュー"
          >
            <span className="w-6 h-0.5 bg-white transition-all duration-300"></span>
            <span className="w-6 h-0.5 bg-white transition-all duration-300"></span>
            <span className="w-6 h-0.5 bg-white transition-all duration-300"></span>
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          style={{ backgroundColor: '#0f2350' }}
        >
          <div className="flex flex-col items-center justify-center h-full gap-8 px-6">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-white text-2xl font-medium transition-all duration-300 hover:text-[#D4AF37] whitespace-nowrap"
                style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
              >
                <span className="flex flex-col items-center">
                  <span>{t(item.labelKey)}</span>
                  {item.sublabelKey && (
                    <span className="text-sm text-white/60 mt-1">{t(item.sublabelKey)}</span>
                  )}
                </span>
              </Link>
            ))}

            {/* Language Switcher - Mobile */}
            <div className="flex items-center gap-6 mt-8">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    changeLanguage(lang.code);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`text-base font-medium transition-all duration-300 whitespace-nowrap ${
                    i18n.language === lang.code
                      ? 'text-white border-b-2 border-[#D4AF37]'
                      : 'text-white/70'
                  }`}
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}